async function requestJson(url, options) {
  const response = await fetch(url, options);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with status ${response.status}`);
  }

  return response.json();
}

export async function searchCombined({
  query,
  sessionLimit = 8,
  chunkLimit = 12,
  sessionTypes = [],
  sessionIds = [],
  sessionId = null,
  searchMode = 'best',
  strictExact = false,
}) {
  const params = new URLSearchParams();

  params.set('q', query);
  params.set('session_limit', String(sessionLimit));
  params.set('chunk_limit', String(chunkLimit));
  params.set('search_mode', searchMode);

  // Only meaningful in Exact mode, but harmless to send otherwise -- the
  // backend only honors it when search_mode is actually 'exact'.
  if (strictExact) {
    params.set('strict_exact', 'true');
  }

  if (sessionTypes.length > 0) {
    params.set('session_type', sessionTypes.join(','));
  }

  if (sessionIds.length > 0) {
    params.set('session_ids', sessionIds.join(','));
  } else if (sessionId) {
    params.set('session_id', String(sessionId));
  }

  return requestJson(`/search/combined?${params.toString()}`);
}


export async function searchAiRankedChunks({
  query,
  candidateLimit = 60,
  limit = 12,
  sessionTypes = [],
  sessionIds = [],
  sessionId = null,
  searchMode = 'best',
  maxChunksPerSession = 3,
}) {
  const params = new URLSearchParams();

  params.set('q', query);
  params.set('candidate_limit', String(candidateLimit));
  params.set('limit', String(limit));
  params.set('search_mode', searchMode);
  params.set('max_chunks_per_session', String(maxChunksPerSession));

  if (sessionTypes.length > 0) {
    params.set('session_type', sessionTypes.join(','));
  }

  if (sessionIds.length > 0) {
    params.set('session_ids', sessionIds.join(','));
  } else if (sessionId) {
    params.set('session_id', String(sessionId));
  }

  return requestJson(`/search/ai-ranked-chunks?${params.toString()}`);
}


// Fetches a grounded, cited answer synthesized from transcript chunks.
// Unlike every other search function here, this one calls an LLM to write
// prose, not just rank/retrieve. `grounded: false` in the response means
// the transcripts don't clearly answer the question -- callers should
// treat that as "no answer available", not as an error.
export async function searchAnswer({
  query,
  chunkLimit = 8,
  maxChunksPerSession = 2,
  sessionTypes = [],
  sessionIds = [],
  applyReranker = true,
  signal = null,
}) {
  const params = new URLSearchParams();

  params.set('q', query);
  params.set('chunk_limit', String(chunkLimit));
  params.set('max_chunks_per_session', String(maxChunksPerSession));
  params.set('apply_reranker', applyReranker ? 'true' : 'false');

  if (sessionTypes.length > 0) {
    params.set('session_type', sessionTypes.join(','));
  }

  if (sessionIds.length > 0) {
    params.set('session_ids', sessionIds.join(','));
  }

  return requestJson(`/search/answer?${params.toString()}`, signal ? { signal } : undefined);
}



export async function searchSmart({
  query,
  sessionLimit = null,
  chunkLimit = null,
  topChunksPerSession = null,
  sessionTypes = [],
  searchMode = 'best',
  applyRecommendedReranker = false,
  candidateSessionLimit = 20,
  candidateChunkLimit = 60,
}) {
  const params = new URLSearchParams();

  params.set('q', query);

  if (searchMode) {
    params.set('search_mode', searchMode);
  }

  params.set('apply_recommended_reranker', applyRecommendedReranker ? 'true' : 'false');

  // These are intentional overrides. Do not send defaults here.
  // If the user asks for "best 3 sessions", the planner should keep session_limit=3.
  if (sessionLimit !== null && sessionLimit !== undefined) {
    params.set('session_limit', String(sessionLimit));
  }

  if (chunkLimit !== null && chunkLimit !== undefined) {
    params.set('chunk_limit', String(chunkLimit));
  }

  if (topChunksPerSession !== null && topChunksPerSession !== undefined) {
    params.set('top_chunks_per_session', String(topChunksPerSession));
  }

  if (applyRecommendedReranker) {
    params.set('candidate_session_limit', String(candidateSessionLimit));
    params.set('candidate_chunk_limit', String(candidateChunkLimit));
  }

  if (sessionTypes.length > 0) {
    params.set('session_type', sessionTypes.join(','));
  }

  return requestJson(`/search/smart?${params.toString()}`);
}

export async function getSessionSegments({
  sessionId,
  startSeconds,
  windowSeconds = 600,
}) {
  const params = new URLSearchParams();

  params.set('start_seconds', String(startSeconds));
  params.set('window_seconds', String(windowSeconds));

  return requestJson(`/sessions/${sessionId}/segments?${params.toString()}`);
}

export async function fetchHighlightTerms({ query, strictExact = false }) {
  const params = new URLSearchParams();

  params.set('q', query);

  if (strictExact) {
    params.set('strict_exact', 'true');
  }

  return requestJson(`/search/highlight-terms?${params.toString()}`);
}


export async function getRecentSearchHistory({ limit = 20 } = {}) {
  const params = new URLSearchParams();

  params.set('limit', String(limit));

  return requestJson(`/users/me/search-history/recent?${params.toString()}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function getSearchHistory({ limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams();

  params.set('limit', String(limit));
  params.set('offset', String(offset));

  return requestJson(`/users/me/search-history/all?${params.toString()}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function deleteSearchHistoryItem({ historyId }) {
  return requestJson(`/users/me/search-history/${historyId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function clearSearchHistory() {
  return requestJson('/users/me/search-history/all', {
    method: 'DELETE',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function getFavorites({
  limit = 50,
  offset = 0,
  favoriteType = null,
  query = '',
} = {}) {
  const params = new URLSearchParams();

  params.set('limit', String(limit));
  params.set('offset', String(offset));

  if (favoriteType && favoriteType !== 'all') {
    params.set('favorite_type', favoriteType);
  }

  const cleanedQuery = String(query || '').trim();

  if (cleanedQuery) {
    params.set('q', cleanedQuery);
  }

  return requestJson(`/users/me/favorites?${params.toString()}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function createFavorite({ favoriteType, sourceId, sourceQuery = null }) {
  return requestJson('/users/me/favorites', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      favorite_type: favoriteType,
      source_id: sourceId,
      source_query: sourceQuery,
    }),
  });
}


export async function getFavoriteStatuses({ items = [] } = {}) {
  return requestJson('/users/me/favorites/status', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: items.map((item) => ({
        favorite_type: item.favoriteType,
        source_id: item.sourceId,
      })),
    }),
  });
}


export async function deleteFavorite({ favoriteId }) {
  return requestJson(`/users/me/favorites/${favoriteId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function getSessions() {
  return requestJson('/sessions');
}

export async function getSessionSummary({ sessionId }) {
  return requestJson(`/sessions/${sessionId}/summary`);
}

export async function getBrowseQaQuestions() {
  return requestJson('/qa/questions');
}

// Fetches the full diarized transcript-segment breakdown for ONE question,
// on demand. /qa/questions itself only returns lightweight metadata --
// this used to be embedded for all ~772 questions up front (a 14.8MB
// payload), now fetched only when a question is actually expanded.
export async function getQaQuestionSegments({ questionId }) {
  return requestJson(`/qa/questions/${questionId}/segments`);
}

export async function getSessionTypes() {
  return requestJson('/session-types');
}


// Classifies whether a query is asking to retrieve a specific already-known
// session (summary / question list / transcript slice) rather than search
// for content across sessions. See SearchIntentResponse in app/schemas.py
// for the exact shape (status: 'search' | 'resolved' | 'ambiguous' | 'no_match').
export async function searchIntent({ query }) {
  const params = new URLSearchParams();

  params.set('q', query);

  return requestJson(`/search/intent?${params.toString()}`);
}


export async function getPipelineStatus({
  start = null,
  end = null,
} = {}) {
  const params = new URLSearchParams();

  if (start) {
    params.set('start', start);
  }

  if (end) {
    params.set('end', end);
  }

  const query = params.toString();

  return requestJson(`/admin/pipeline-status${query ? `?${query}` : ''}`);
}



export async function getNotes({
  limit = 100,
  offset = 0,
  noteType = null,
  query = '',
} = {}) {
  const params = new URLSearchParams();

  params.set('limit', String(limit));
  params.set('offset', String(offset));

  if (noteType && noteType !== 'all') {
    params.set('note_type', noteType);
  }

  const cleanedQuery = String(query || '').trim();

  if (cleanedQuery) {
    params.set('q', cleanedQuery);
  }

  return requestJson(`/users/me/notes?${params.toString()}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function getNoteBySource({ noteType, sourceId }) {
  return requestJson(`/users/me/notes/by-source/${noteType}/${sourceId}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function saveNoteBySource({
  noteType,
  sourceId,
  noteHtml,
  sourceQuery = null,
}) {
  return requestJson(`/users/me/notes/by-source/${noteType}/${sourceId}`, {
    method: 'PUT',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      note_html: noteHtml,
      source_query: sourceQuery,
    }),
  });
}


export async function getNoteStatuses({ items = [] } = {}) {
  return requestJson('/users/me/notes/status', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: items.map((item) => ({
        note_type: item.noteType,
        source_id: item.sourceId,
      })),
    }),
  });
}


export async function getNote({ noteId }) {
  return requestJson(`/users/me/notes/${noteId}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}


export async function updateNote({
  noteId,
  noteHtml,
  sourceQuery = null,
}) {
  return requestJson(`/users/me/notes/${noteId}`, {
    method: 'PUT',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      note_html: noteHtml,
      source_query: sourceQuery,
    }),
  });
}


export async function deleteNote({ noteId }) {
  return requestJson(`/users/me/notes/${noteId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export async function getAdminUploadSessions() {
  return requestJson('/admin/upload-sessions', {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export async function guessAdminUploadSession({ filename }) {
  const params = new URLSearchParams();
  params.set('filename', filename);

  return requestJson(`/admin/upload-sessions/guess?${params.toString()}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export function uploadAdminSession({
  file,
  sessionType,
  sessionDate,
  onProgress = null,
}) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('session_type', sessionType);
    form.append('session_date', sessionDate);
    form.append('file', file, file.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/admin/upload-sessions');
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || typeof onProgress !== 'function') {
        return;
      }

      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };

    xhr.onerror = () => {
      reject(new Error('The upload failed because the network connection was interrupted.'));
    };

    xhr.onload = () => {
      let data = null;

      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch (err) {
        data = null;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data || {});
        return;
      }

      const detail = data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (xhr.responseText || `Upload failed with status ${xhr.status}`);

      reject(new Error(message));
    };

    xhr.send(form);
  });
}

export async function getAdminTranscriptDownloads() {
  return requestJson('/admin/transcript-downloads', {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export async function downloadAdminTranscripts({
  sessionTypes,
  sessionDates,
  tagIds = [],
  tagMatch = 'or',
  includeTimestamps = false,
}) {
  const response = await fetch('/admin/transcript-downloads/download', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'text/plain',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      session_types: sessionTypes,
      session_dates: sessionDates,
      tag_ids: tagIds,
      tag_match: tagMatch,
      include_timestamps: Boolean(includeTimestamps),
    }),
  });

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    const text = await response.text();

    if (text) {
      try {
        const data = JSON.parse(text);
        if (typeof data?.detail === 'string') {
          message = data.detail;
        } else {
          message = text;
        }
      } catch (err) {
        message = text;
      }
    }

    throw new Error(message);
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] || 'davealyzer_transcripts.txt';

  return {
    blob: await response.blob(),
    filename,
  };
}


// Personal tags -----------------------------------------------------------

export async function getUserTags({ query = '' } = {}) {
  const params = new URLSearchParams();
  const cleanedQuery = String(query || '').trim();

  if (cleanedQuery) {
    params.set('q', cleanedQuery);
  }

  const suffix = params.toString();
  return requestJson(`/users/me/tags${suffix ? `?${suffix}` : ''}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export async function createUserTag({ name }) {
  return requestJson('/users/me/tags', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });
}

export async function deleteUserTag({ tagId }) {
  return requestJson(`/users/me/tags/${tagId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export async function getUserTagAssignments({ tagId }) {
  return requestJson(`/users/me/tags/${tagId}/assignments`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export async function getTagsForSource({ targetType, sourceId }) {
  return requestJson(`/users/me/tags/by-source/${targetType}/${sourceId}`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}

export async function getTagStatuses({ items = [] } = {}) {
  return requestJson('/users/me/tags/status', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: items.map((item) => ({
        target_type: item.targetType || item.favoriteType,
        source_id: item.sourceId,
      })),
    }),
  });
}

export async function getSessionTagStatuses({ sessionIds = [] } = {}) {
  return requestJson('/users/me/tags/session-status', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      session_ids: sessionIds,
    }),
  });
}

export async function createTagAssignment({
  tagId,
  targetType,
  sourceId,
  sourceQuery = null,
}) {
  return requestJson(`/users/me/tags/${tagId}/assignments`, {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      target_type: targetType,
      source_id: sourceId,
      source_query: sourceQuery,
    }),
  });
}

export async function deleteTagAssignment({ assignmentId }) {
  return requestJson(`/users/me/tags/assignments/${assignmentId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
}
